import { z } from 'zod';

import { entityIdSchema, isoDateTimeSchema, moneyFilsSchema } from '../primitives/schema';

/**
 * Fund policy — the configurable rules of the insurance fund.
 *
 * The project owner asked for a settings screen to define what employees
 * declare and which dependents qualify. That makes these **data, not code**:
 * the committee changes the policy each year without a code change, and last
 * year's declarations stay interpretable against the version they were made
 * under.
 *
 * Promotion reason (§5.2, §3.1): this is not the settings feature's schema
 * moved here for convenience. The fund's rules are domain policy that every
 * feature obeys — employees for dependent eligibility, declarations for the
 * item list, claims and costs for coverage tiers. The settings feature owns the
 * UI that edits it, not the policy itself. `core/` owns cross-feature domain
 * schemas and policies, so it lives here.
 *
 * Only `committee_manager` may write it (`canEditFundPolicy`), enforced in
 * security rules and a Cloud Function — never by this schema alone.
 */

/** How a declaration item is answered. Drives both the form and validation. */
export const declarationFieldTypeSchema = z.enum([
  'boolean', // "هل تعاني من مرض مزمن؟"
  'text', // free text detail
  'date', // e.g. date of a past operation
  'select', // one of `options`
  'multiselect', // any of `options`
  'number',
]);

export const declarationItemSchema = z
  .object({
    id: entityIdSchema,
    /** Question shown to the employee, in Arabic. */
    labelAr: z.string().trim().min(2, 'نص البند مطلوب').max(300),
    helpAr: z.string().trim().max(500).optional(),
    type: declarationFieldTypeSchema,
    required: z.boolean(),
    /**
     * Medical detail rather than administrative fact. Sensitive items are
     * readable only by roles passing `canReadMedicalDetail` (ADR-004) and are
     * stored in the separate medical subcollection.
     */
    sensitive: z.boolean(),
    options: z.array(z.string().trim().min(1).max(120)).max(50).optional(),
    /** Display order on the form. */
    order: z.number().int().min(0).max(999),
    /** Retired items stay for historical declarations but are not offered. */
    active: z.boolean(),
  })
  .refine(
    (item) =>
      !['select', 'multiselect'].includes(item.type) ||
      (item.options !== undefined && item.options.length > 0),
    { message: 'بنود الاختيار تتطلب قائمة خيارات', path: ['options'] },
  );

/** Relationships the fund may cover. The committee decides which are enabled. */
export const dependentRelationSchema = z.enum([
  'spouse',
  'son',
  'daughter',
  'father',
  'mother',
]);

export const dependentRuleSchema = z
  .object({
    relation: dependentRelationSchema,
    allowed: z.boolean(),
    /** Inclusive upper age bound, when the relationship has one. */
    maxAgeYears: z.number().int().min(0).max(120).optional(),
    /** Cap for this relationship, e.g. one spouse. */
    maxCount: z.number().int().min(0).max(20).optional(),
    requiresProofDocument: z.boolean(),
  })
  .refine((rule) => !rule.allowed || rule.maxCount !== 0, {
    message: 'لا يمكن السماح بالصلة مع تحديد الحد الأقصى صفرًا',
    path: ['maxCount'],
  });

export const coverageTierSchema = z.object({
  id: entityIdSchema,
  labelAr: z.string().trim().min(2).max(120),
  /** Annual fund contribution per covered person, in fils. */
  annualCostPerPersonFils: moneyFilsSchema,
  /** Ceiling the fund pays per person per policy year, in fils. */
  annualCeilingFils: moneyFilsSchema,
  active: z.boolean(),
});

export const fundPolicySchema = z
  .object({
    /** Schema version — §6.2 requires persisted schemas to carry one. */
    schemaVersion: z.literal(1),

    /** e.g. "2026-2027". Declarations record which policy year they answered. */
    policyYear: z.string().trim().regex(/^\d{4}-\d{4}$/, 'صيغة السنة: YYYY-YYYY'),

    declarationItems: z
      .array(declarationItemSchema)
      .max(100, 'عدد بنود الإقرار أكبر من المسموح')
      .refine(
        (items) => new Set(items.map((item) => item.id)).size === items.length,
        'معرّفات بنود الإقرار مكررة',
      ),

    dependentRules: z
      .array(dependentRuleSchema)
      .refine(
        (rules) => new Set(rules.map((rule) => rule.relation)).size === rules.length,
        'قواعد المعالين مكررة لنفس الصلة',
      ),

    /**
     * Cap across all relationships. Bounded because it also bounds how many
     * documents a single employee read fans out to — an unbounded value is a
     * cost defect as much as a policy one (§11.5.1, ADR-003).
     */
    maxDependentsPerEmployee: z.number().int().min(0).max(30),

    coverageTiers: z
      .array(coverageTierSchema)
      .min(1, 'يجب تعريف فئة تغطية واحدة على الأقل')
      .max(10)
      .refine(
        (tiers) => new Set(tiers.map((tier) => tier.id)).size === tiers.length,
        'معرّفات فئات التغطية مكررة',
      ),

    /** Window during which employees may submit or amend declarations. */
    declarationWindow: z
      .object({
        opensAt: isoDateTimeSchema,
        closesAt: isoDateTimeSchema,
      })
      .refine((window) => window.opensAt < window.closesAt, {
        message: 'تاريخ الإغلاق يجب أن يكون بعد تاريخ الفتح',
        path: ['closesAt'],
      }),

    /**
     * Emergency stop (§11.5.2). Flipping this off blocks employee writes at the
     * rules layer without a deploy.
     */
    employeeSubmissionsEnabled: z.boolean(),

    updatedAt: isoDateTimeSchema,
    /** Set from the verified server identity, never from the client (§4.2). */
    updatedBy: entityIdSchema,
  })
  .refine(
    (policy) => policy.declarationItems.filter((item) => item.active).length > 0,
    { message: 'يجب تفعيل بند إقرار واحد على الأقل', path: ['declarationItems'] },
  );

export type DeclarationItem = z.infer<typeof declarationItemSchema>;
export type DeclarationFieldType = z.infer<typeof declarationFieldTypeSchema>;
export type DependentRelation = z.infer<typeof dependentRelationSchema>;
export type DependentRule = z.infer<typeof dependentRuleSchema>;
export type CoverageTier = z.infer<typeof coverageTierSchema>;
export type FundPolicy = z.infer<typeof fundPolicySchema>;
